import { MarkerConfig } from '@photo-sphere-viewer/markers-plugin';
import { ReactNode } from 'react';
import { convertToSlug } from './generic';

const getMarkerHtml = () => `<span class="relative flex h-5 w-5">
  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
  <span class="relative inline-flex rounded-full h-5 w-5 bg-white"></span>
</span>`;

const getTooltipHtml = (title: string, text: ReactNode) =>
  `<div class="custom-tooltip__content">
    <h4>${title}</h4>
    <p>${text}</p>
  </div>`;

const getTooltip = (
  title: string,
  text: ReactNode,
  ref: HTMLDivElement,
  position?: 'top' | 'right' | 'left' | 'bottom',
) => {
  return {
    content: getTooltipHtml(title, text),
    // element: document?.querySelector('#tooltip-content')?.innerHTML,
    // content: document?.querySelector('#tooltip-content')?.innerHTML,
    // content: document?.querySelector('#tooltip-content')?.innerText,
    // content: ref?.current?.innerHTML,
    className: 'custom-tooltip',
    position,
    trigger: 'click',
  };
};

type PixelPositionType = { x: number; y: number };
type RelationType = {
  room: string;
  choice: string[];
};

export type MarkersProps = {
  text: ReactNode;
  title: string;
  ligtingSolutionSectionId?: string;
  markers: {
    positions: PixelPositionType[];
    relation: RelationType;
  };
};

const calcRadiusValueFromPixels = (
  image: { width: number; height: number },
  x: number,
  y: number,
): { pitch: number; yaw: number } => {
  const yaw = ((image.width - x) / image.width) * -Math.PI;
  const pitch = (((image.height - y) / image.height) * Math.PI) / 2;

  return {
    pitch,
    yaw,
  };
};

const getMarkerId = (marker: MarkersProps) =>
  `marker-${convertToSlug(marker.title)}-${marker.markers.relation.room}-${marker.markers.positions.map((pos) => pos.x + '-' + pos.y)}`;

const getListOfMarkers = (
  markers: MarkersProps[],
  ref: any,
): (MarkerConfig & { title: string })[] => {
  const list = markers.flatMap((marker) => {
    return marker.markers.positions.map((position) => {
      const pos = calcRadiusValueFromPixels(
        { width: 2048, height: 1024 },
        position.x,
        position.y,
      );

      return {
        id: getMarkerId(marker),
        title: marker.title,
        // position: { yaw: position.x * -1, pitch: position.y },
        position: { yaw: pos.yaw, pitch: pos.pitch },
        html: getMarkerHtml(),
        anchor: 'bottom right',
        scale: [1, 1],
        zoomLvl: 1,
        tooltip: getTooltip(marker.title, marker.text, ref),
      };
    });
  }) as (MarkerConfig & { title: string })[];

  return list;
};

const getSolutionSlugs = (solutions: Solutions, keys: string[]) => {
  const pendants = Object.entries(solutions.pendant)
    .filter(([key]) => keys.some((k) => key.match(k)))
    .map(([, value]) => value);

  const recessed = Object.entries(solutions.recessed)
    .filter(([key]) => keys.some((k) => key.match(k)))
    .map(([, value]) => value);

  return pendants.concat(recessed);
};

type RoomType =
  | 'openOffice'
  | 'individualOffice'
  | 'classroom'
  | 'conferenceRoom';

type RecessedOptions =
  | 'recessed'
  | 'downlight'
  | 'linear'
  | 'linearTw'
  | 'linear-tunable-white'
  | 'linear-with-spotlights'
  | 'linear-with-spotlights-pendants'
  | 'tunable-white'
  | 'with-spotlights'
  | 'with-spotlights-accent'
  | 'recessed-linear-with-spotlights'
  | 'linear-with-spotlights-and-pendants';
type PendantOptions =
  | 'pendant'
  | 'ddl-tunable-white-with-wallwasher-accent'
  | 'ddl-wallwasher'
  | 'tunable-white'
  | 'with-downlight'
  | 'with-downlight-tunable-white'
  | 'with-spotlights'
  | 'with-wallwasher-accent'
  | 'with-wallwasher-downlights'
  | 'with-wallwasher-downlights-tunable-white'
  | 'pendant-with-downlight'
  | 'pendant-with-downlight-tunable-white'
  | 'pendant-with-spotlights'
  | 'pendant-with-wallwasher-and-downlight'
  | 'pendant-with-wallwasher-and-downlight-tunable-white';

type Solutions = {
  recessed: Record<RecessedOptions, string>;
  pendant: Record<PendantOptions, string>;
};

type TextCategory =
  | 'recessed'
  | 'pendant'
  | 'downlight'
  | 'spotlight'
  | 'wallwasher'
  | 'ddl'
  | 'tunableWhite'
  | 'accent';

type Texts = Record<TextCategory, { title: string; text: string }>;

export type MarkersGeneralProps = {
  rooms: Record<RoomType, string>;
  solutions: Solutions;
  texts: Texts;
};

export type MarkerType = {
  title: string;
  text: string;
  positions: PixelPositionType[];
  relation: RelationType;
};

const getData = (markersGeneral: MarkersGeneralProps): MarkerType[] => {
  const markerChoiceSlugs = {
    tunableWhite: getSolutionSlugs(markersGeneral.solutions, [
      'tw',
      'tunable-white',
      'tunablewhite',
    ]),
    spotlight: getSolutionSlugs(markersGeneral.solutions, ['spotlight']),
    pendant: getSolutionSlugs(markersGeneral.solutions, ['pendant']),
    accent: getSolutionSlugs(markersGeneral.solutions, ['accent']),
    wallwasher: getSolutionSlugs(markersGeneral.solutions, [
      'wallwasher',
      'ww',
    ]),
    downlight: getSolutionSlugs(markersGeneral.solutions, ['downlight']),
    ddl: getSolutionSlugs(markersGeneral.solutions, ['ddl']),
  };
  // console.log('yo -> markerChoiceSlugs', markerChoiceSlugs);

  const markersData = [
    /* OPEN OFFICE */
    /* Recessed */
    {
      title: markersGeneral.texts.recessed.title,
      text: markersGeneral.texts.recessed.text,
      positions: [{ x: 1811, y: 879 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: Object.values(markersGeneral.solutions.recessed),
      },
    },
    /* Spotlights */
    {
      title: markersGeneral.texts.spotlight.title,
      text: markersGeneral.texts.spotlight.text,
      positions: [{ x: 2054, y: 946 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: markerChoiceSlugs.spotlight,
      },
    },
    /* Accent */
    {
      title: markersGeneral.texts.accent.title,
      text: markersGeneral.texts.accent.text,
      positions: [{ x: 2362, y: 991 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: markerChoiceSlugs.accent,
      },
    },
    /* Tunable white */
    {
      title: markersGeneral.texts.tunableWhite.title,
      text: markersGeneral.texts.tunableWhite.text,
      positions: [{ x: 1841, y: 1005 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: markerChoiceSlugs.tunableWhite,
      },
    },

    /* Pendant  */
    {
      title: markersGeneral.texts.pendant.title,
      text: markersGeneral.texts.pendant.text,
      positions: [{ x: 1799, y: 974 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: Object.values(markersGeneral.solutions.pendant),
      },
    },

    /* wallwasher */
    {
      title: markersGeneral.texts.wallwasher.title,
      text: markersGeneral.texts.wallwasher.text,
      positions: [{ x: 2073, y: 928 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: markerChoiceSlugs.wallwasher,
      },
    },

    /* DDL */
    {
      title: markersGeneral.texts.ddl.title,
      text: markersGeneral.texts.ddl.text,
      positions: [{ x: 1735, y: 978 }],
      relation: {
        room: markersGeneral.rooms.openOffice,
        choice: markerChoiceSlugs.ddl,
      },
    },

    /* INDIVIDUAL OFFICE */

    /* Recessed */
    {
      title: markersGeneral.texts.recessed.title,
      text: markersGeneral.texts.recessed.text,
      positions: [{ x: 2061, y: 780 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: Object.values(markersGeneral.solutions.recessed),
      },
    },
    /* Spotlights */
    {
      title: markersGeneral.texts.spotlight.title,
      text: markersGeneral.texts.spotlight.text,
      positions: [{ x: 1533, y: 680 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: markerChoiceSlugs.spotlight,
      },
    },
    /* Accent */
    {
      title: markersGeneral.texts.accent.title,
      text: markersGeneral.texts.accent.text,
      positions: [{ x: 2644, y: 884 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: markerChoiceSlugs.accent,
      },
    },
    /* Tunable white */
    {
      title: markersGeneral.texts.tunableWhite.title,
      text: markersGeneral.texts.tunableWhite.text,
      positions: [{ x: 2228, y: 841 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: markerChoiceSlugs.tunableWhite,
      },
    },

    /* Pendant  */
    {
      title: markersGeneral.texts.pendant.title,
      text: markersGeneral.texts.pendant.text,
      positions: [{ x: 2155, y: 847 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: Object.values(markersGeneral.solutions.pendant),
      },
    },

    /* wallwasher */
    {
      title: markersGeneral.texts.wallwasher.title,
      text: markersGeneral.texts.wallwasher.text,
      positions: [{ x: 1532, y: 665 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: markerChoiceSlugs.wallwasher,
      },
    },

    /* DDL */
    {
      title: markersGeneral.texts.ddl.title,
      text: markersGeneral.texts.ddl.text,
      positions: [{ x: 1994, y: 919 }],
      relation: {
        room: markersGeneral.rooms.individualOffice,
        choice: markerChoiceSlugs.ddl,
      },
    },

    /* CONFERENCE ROOM */
    /* Recessed */
    {
      title: markersGeneral.texts.recessed.title,
      text: markersGeneral.texts.recessed.text,
      positions: [{ x: 1878, y: 721 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: Object.values(markersGeneral.solutions.recessed),
      },
    },
    /* Spotlights */
    {
      title: markersGeneral.texts.spotlight.title,
      text: markersGeneral.texts.spotlight.text,
      positions: [{ x: 1657, y: 627 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: markerChoiceSlugs.spotlight,
      },
    },
    /* Accent */
    {
      title: markersGeneral.texts.accent.title,
      text: markersGeneral.texts.accent.text,
      positions: [{ x: 2026, y: 802 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: markerChoiceSlugs.accent,
      },
    },
    /* Tunable white */
    {
      title: markersGeneral.texts.tunableWhite.title,
      text: markersGeneral.texts.tunableWhite.text,
      positions: [{ x: 2146, y: 984 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: markerChoiceSlugs.tunableWhite,
      },
    },

    /* Pendant  */
    {
      title: markersGeneral.texts.pendant.title,
      text: markersGeneral.texts.pendant.text,
      positions: [{ x: 2003, y: 854 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: [
          ...Object.values(markersGeneral.solutions.pendant),
          ...markerChoiceSlugs.pendant,
        ],
      },
    },

    /* wallwasher */
    {
      title: markersGeneral.texts.wallwasher.title,
      text: markersGeneral.texts.wallwasher.text,
      positions: [{ x: 1830, y: 711 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: markerChoiceSlugs.wallwasher,
      },
    },

    /* Downlight */
    {
      title: markersGeneral.texts.downlight.title,
      text: markersGeneral.texts.downlight.text,
      positions: [{ x: 2230, y: 711 }],
      relation: {
        room: markersGeneral.rooms.conferenceRoom,
        choice: markerChoiceSlugs.downlight,
      },
    },

    /* CLASSROOM */
    /* Recessed */
    {
      title: markersGeneral.texts.recessed.title,
      text: markersGeneral.texts.recessed.text,
      positions: [{ x: 1841, y: 829 }],
      relation: {
        room: markersGeneral.rooms.classroom,
        choice: Object.values(markersGeneral.solutions.recessed),
      },
    },
    /* Spotlights */
    {
      title: markersGeneral.texts.spotlight.title,
      text: markersGeneral.texts.spotlight.text,
      positions: [{ x: 2036, y: 915 }],
      relation: {
        room: markersGeneral.rooms.classroom,
        choice: markerChoiceSlugs.spotlight,
      },
    },

    /* Tunable white */
    {
      title: markersGeneral.texts.tunableWhite.title,
      text: markersGeneral.texts.tunableWhite.text,
      positions: [{ x: 1801, y: 1025 }],
      relation: {
        room: markersGeneral.rooms.classroom,
        choice: markerChoiceSlugs.tunableWhite,
      },
    },

    /* Pendant  */
    {
      title: markersGeneral.texts.pendant.title,
      text: markersGeneral.texts.pendant.text,
      positions: [{ x: 1815, y: 890 }],
      relation: {
        room: markersGeneral.rooms.classroom,
        choice: Object.values(markersGeneral.solutions.pendant),
      },
    },
  ] as MarkerType[];

  return markersData;
};

const getConfig = (items: MarkerType[]): MarkersProps[] =>
  items.map((item) => getMarkerConfig(item));

const getMarkerConfig = ({
  title,
  text,
  relation,
  positions,
}: MarkerType): MarkersProps => {
  const markerConfig = {
    markers: {
      positions,
      relation: relation,
    },
    title: title,
    text: text,
  } as MarkersProps;

  return markerConfig;
};

export const markers = {
  list: getListOfMarkers,
  html: getMarkerHtml,
  tooltip: getTooltip,
  config: getConfig,
  getMarkerID: getMarkerId,
  data: getData,
};
